<template>
  <div
    ref="rootRef"
    @focus.capture="onFocus"
    @blur.capture="onBlur"
  >
    <DSPopover v-model="openModelValue">
      <template #activator="{ setRef }">
        <DSFormField
          :id="id"
          :label="props.label"
          :inline-edit="props.inlineEdit"
          :tooltip="props.tooltip"
          :hidden-label="props.hiddenLabel"
          :required="props.required"
          :disabled="props.disabled"
          :error-messages="dsFormField.errorMessages.value"
          :name="props.name"
        >
          <DSFormFieldGroup :ref="el => setRef((el as ComponentPublicInstance)?.$el)">
            <DSBaseInput
              :id="id"
              ref="dateTimeInputRef"
              v-model="inputModelValue"
              type="text"
              autocomplete="off"
              :data-cy-suffix="props.name"
              :label="props.label"
              :placeholder="props.placeholder"
              :inline-edit="props.inlineEdit"
              :disabled="props.disabled"
              :has-error="!!dsFormField.errorMessages.value.length"
              :required="props.required"
              :clearable="props.clearable"
              @click="onInputClick"
              @keydown.tab="onTab"
              @keydown.enter.prevent="onEnter"
              @clear="clear"
            />
            <DSButton
              :class="DS_FORM_FIELD_CLASSES.InlineAction"
              :aria-expanded="openModelValue"
              :disabled="props.disabled"
              :prefix-icon="props.actionIcon"
              data-cy="ds-date-time-form-field__action-button"
              variant="secondary"
              @mousedown="props.inlineEdit && $event.preventDefault()"
              @click="openModelValue = !openModelValue"
            />
          </DSFormFieldGroup>
        </DSFormField>
      </template>

      <template #content>
        <div ref="contentRef">
          <slot />
        </div>
      </template>
    </DSPopover>
  </div>
</template>

<script setup lang="ts">
import { ComponentPublicInstance, computed, ref, watch } from "vue";
import { useDSInput } from "@/src/design_system/form_elements/shared/composables/useDSInput";
import { crossesFocusBoundary } from "@/src/design_system/form_elements/shared/utils/focusBoundary";
import DSPopover from "@/src/design_system/primitives/popover/components/DSPopover.vue";
import type {
  DSDateTimeFormFieldEmits,
  DSDateTimeFormFieldInputModelValue,
  DSDateTimeFormFieldOpenModelValue,
  DSDateTimeFormFieldProps,
} from "@/src/design_system/form_elements/shared_modules/date_time_form_field/types";
import DSBaseInput from "@/src/design_system/form_elements/base_input/components/DSBaseInput.vue";
import DSFormField from "@/src/design_system/form_elements/form_field/components/DSFormField.vue";
import { DS_FORM_FIELD_CLASSES } from "@/src/design_system/form_elements/form_field/constants/classes";
import DSFormFieldGroup from "@/src/design_system/form_elements/form_field_group/components/DSFormFieldGroup.vue";
import DSButton from "@/src/design_system/button_and_controls/button/components/DSButton.vue";
import { useInputPopoverInteractions } from "@/src/design_system/form_elements/shared/composables/useInputPopoverInteractions";
import { useDSFormField } from "@/src/design_system/form_elements/shared/composables/useDSFormField";
import { useDSFormFieldId } from "@/src/design_system/form_elements/shared/composables/useDSFormFieldId";

const props = withDefaults(defineProps<DSDateTimeFormFieldProps>(), {
  hiddenLabel: false,
  inlineEdit: false,
  disabled: false,
  tooltip: undefined,
  placeholder: undefined,
  required: false,
  clearable: true,
  rules: undefined,
});

const emits = defineEmits<DSDateTimeFormFieldEmits>();

const dateTimeInputRef = ref<InstanceType<typeof DSBaseInput>>();
const rootRef = ref<HTMLElement>();
const contentRef = ref<HTMLElement>();
const { focus } = useDSInput(dateTimeInputRef);

const blur = () => {
  const activeElement = document.activeElement;
  if (activeElement instanceof HTMLElement && rootRef.value?.contains(activeElement)) activeElement.blur();
};

const onFocus = (event: Event) => {
  if (!crossesFocusBoundary(event, rootRef.value)) return;
  emits("focus", event);
};

const onBlur = (event: Event) => {
  if (!crossesFocusBoundary(event, rootRef.value)) return;
  emits("blur", event);
  openModelValue.value = false;
};

const inputModelValue = defineModel<DSDateTimeFormFieldInputModelValue>("input", { required: true });
const openModelValue = defineModel<DSDateTimeFormFieldOpenModelValue>("open", { required: true });
watch(openModelValue, (isOpen, wasOpen) => {
  if (isOpen || !wasOpen) return;
  // Restore before the focused popover control is removed from the DOM.
  if (contentRef.value?.contains(document.activeElement)) focus();
  emits("close");
});

const { onInputClick, onTab, onEnter } = useInputPopoverInteractions(openModelValue, { toggleOnClick: false });

const clear = () => {
  inputModelValue.value = "";
};

const dsFormField = useDSFormField<DSDateTimeFormFieldInputModelValue>({
  name: computed(() => props.name),
  errorMessages: computed(() => props.errorMessages),
  value: inputModelValue,
  rules: computed(() => props.rules),
});

const { id } = useDSFormFieldId({ props });

defineExpose({ focus, blur });
</script>
